import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Bell, CheckCircle, XCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, getAuthHeaders } from "@/lib/api";

const POLL_INTERVAL_MS = 30_000; // 30s

export default function NotificationBell({ onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const dropdownRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications?limit=15`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const list = data.notifications || [];
      setNotifications(list);
      setUnread(data.unread_count || 0);
      // Fire toasts for newly arrived unread items (skip on first mount to avoid flood)
      if (!firstLoadRef.current) {
        for (const n of list) {
          if (!n.read && !seenIdsRef.current.has(n.id)) {
            if (n.type === "video_ready") {
              toast.success(n.title, {
                description: n.message,
                duration: 7000,
                action: n.link ? { label: "View", onClick: () => handleClick(n) } : undefined,
              });
            } else if (n.type === "video_failed") {
              toast.error(n.title, { description: n.message, duration: 9000 });
            } else {
              toast(n.title, { description: n.message });
            }
          }
          seenIdsRef.current.add(n.id);
        }
      } else {
        list.forEach((n) => seenIdsRef.current.add(n.id));
        firstLoadRef.current = false;
      }
    } catch (_) {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchAll]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (_) {}
  };

  const removeOne = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${id}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnread((c) => {
        const n = notifications.find((x) => x.id === id);
        return n && !n.read ? Math.max(0, c - 1) : c;
      });
    } catch (_) {}
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link && onNavigate) onNavigate(n.link);
    setOpen(false);
  };

  const timeAgo = (iso) => {
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 1000;
      if (diff < 60) return `${Math.floor(diff)}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch { return ""; }
  };

  const iconFor = (type) => {
    if (type === "video_ready") return <CheckCircle size={18} weight="fill" className="text-emerald-500" />;
    if (type === "video_failed") return <XCircle size={18} weight="fill" className="text-rose-500" />;
    return <Bell size={18} className="text-[#E91E8C]" />;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label="Notifications"
        className="relative p-2 rounded-lg hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 transition-colors"
      >
        <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-[#E91E8C] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <h3 className="text-sm font-semibold text-zinc-900">Notifications</h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-xs text-[#E91E8C] hover:underline font-medium"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center text-zinc-400">
                <Bell size={32} weight="thin" className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet.</p>
                <p className="text-xs mt-1">We'll alert you when your videos are ready.</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full flex gap-3 px-4 py-3 text-left hover:bg-zinc-50 border-b border-zinc-50 last:border-b-0 transition-colors ${
                    !n.read ? "bg-[#E91E8C]/5" : ""
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${!n.read ? "font-semibold text-zinc-900" : "text-zinc-700"} truncate`}>
                        {n.title}
                      </p>
                      <span className="text-[11px] text-zinc-400 whitespace-nowrap flex-shrink-0">
                        {timeAgo(n.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.message}</p>
                  </div>
                  <button
                    onClick={(e) => removeOne(n.id, e)}
                    className="opacity-0 group-hover:opacity-100 text-zinc-300 hover:text-zinc-600 flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
